import { useState, useCallback } from 'react';
import { sdsAssistantApi } from '../services/sdsAssistantApi';
import type { SdsChatMessage, SdsSource } from '../types/sdsAssistant';

/**
 * Chat state for the SDS assistant.
 *
 * Holds the sources and the reasoning summary of the LAST answer — that is what the
 * sources panel on the right displays. Older answers keep their inline [n] markers, but
 * the panel always mirrors the most recent turn (the plan's "permanent panel" model).
 */
export function useSdsAssistant() {
    const [messages, setMessages] = useState<SdsChatMessage[]>([]);
    const [sources, setSources] = useState<SdsSource[]>([]);
    const [reasoning, setReasoning] = useState<string | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const sendMessage = useCallback(async (content: string) => {
        if (!content.trim() || isSending) return;

        const userMessage: SdsChatMessage = { role: 'user', content: content.trim() };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setIsSending(true);
        setError(null);

        try {
            const result = await sdsAssistantApi.chat(updatedMessages);
            setMessages(prev => [...prev, { role: 'assistant', content: result.reply }]);
            setSources(result.sources);
            setReasoning(result.reasoning);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to get an answer');
        } finally {
            setIsSending(false);
        }
    }, [messages, isSending]);

    const clearChat = useCallback(() => {
        setMessages([]);
        setSources([]);
        setReasoning(null);
        setError(null);
    }, []);

    return { messages, sources, reasoning, isSending, error, sendMessage, clearChat };
}
